// The visitor's cookie consent: how it is stored, when it must be asked again,
// and how it reaches Google Consent Mode v2.
//
// The defaults (everything but strictly necessary storage denied) are set by an
// inline script the Go server injects before the Tag Manager loader; it also
// re-applies a stored choice. This module is the client side of the same
// record: the banner reads, persists and updates it. Keep CONSENT_STORAGE_KEY
// and CONSENT_MAX_AGE_MS in sync with consentStorageKey and consentMaxAgeMs in
// custom-website-be/main.go.

import { CookiePolicy } from "../components/Types/Configuration";

export const CONSENT_STORAGE_KEY = "site-consent";
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

// Dispatched on window after a choice is saved (detail = ConsentPreferences).
export const CONSENT_CHANGE_EVENT = "site-consent:change";
// Dispatch on window to reopen the banner in its settings view.
export const OPEN_SETTINGS_EVENT = "site-consent:open-settings";

export type ConsentPreferences = {
  analytics: boolean;
  marketing: boolean;
};

// Vendor ids grouped by the consent category that gates them.
export type DeclaredVendors = {
  analytics: string[];
  marketing: string[];
};

// What localStorage holds. `vendors` is what the visitor was told they were
// consenting to, so a vendor added later can trigger a new prompt.
export type StoredConsent = ConsentPreferences & {
  v: number;
  ts: number;
  vendors: string[];
};

// What the server injected alongside the Tag Manager loader.
export type SiteTags = {
  gtmId: string;
  vendors: DeclaredVendors;
};

declare global {
  interface Window {
    __SITE_TAGS__?: SiteTags;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const NO_VENDORS: DeclaredVendors = { analytics: [], marketing: [] };

export function siteTags(): SiteTags | undefined {
  if (typeof window === "undefined") return undefined;
  const tags = window.__SITE_TAGS__;
  return tags && typeof tags.gtmId === "string" ? tags : undefined;
}

export function declaredVendors(policy: CookiePolicy | undefined): DeclaredVendors {
  const declared: DeclaredVendors = { analytics: [], marketing: [] };
  for (const vendor of policy?.vendors ?? []) {
    if (vendor.category === "analytics") declared.analytics.push(vendor.id);
    if (vendor.category === "marketing") declared.marketing.push(vendor.id);
  }
  return declared;
}

// The stored choice, or null when there is none worth honouring: missing,
// unparsable, another record version, or older than CONSENT_MAX_AGE_MS.
export function readConsent(now: number = Date.now()): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.v !== CONSENT_VERSION || typeof parsed.ts !== "number") return null;
    // A record stamped in the future is not fresh, it is broken: a clock change
    // or a tampered value would otherwise suppress the prompt for over a year.
    const age = now - parsed.ts;
    if (age < 0 || age > CONSENT_MAX_AGE_MS) return null;
    return {
      v: CONSENT_VERSION,
      ts: parsed.ts,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      vendors: Array.isArray(parsed.vendors) ? parsed.vendors.filter((v) => typeof v === "string") : [],
    };
  } catch {
    return null;
  }
}

// Whether the banner must ask. A visitor who granted a category is asked again
// when a vendor they never saw appears under it; one who declined it is not,
// since nothing they refused has changed. A vendor that disappeared never
// prompts.
export function needsPrompt(stored: StoredConsent | null, declared: DeclaredVendors): boolean {
  if (!stored) return true;
  const known = new Set(stored.vendors);
  const uncovered = (ids: string[]) => ids.some((id) => !known.has(id));
  if (stored.analytics && uncovered(declared.analytics)) return true;
  if (stored.marketing && uncovered(declared.marketing)) return true;
  return false;
}

// Push the choice to Consent Mode. gtag is defined by the server's inline
// script; without a container (or in preview) there is nothing to update.
export function applyConsent(prefs: ConsentPreferences): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    // Denied by default in the server's bootstrap, so it has to move with the
    // marketing choice here or the two paths disagree.
    personalization_storage: prefs.marketing ? "granted" : "denied",
  });
}

// Persist (unless told not to, as in the admin preview), apply, then announce,
// in that order: a listener reacting to the change must find Consent Mode
// already updated.
export function saveConsent(
  prefs: ConsentPreferences,
  declared: DeclaredVendors,
  options: { persist: boolean } = { persist: true },
): StoredConsent {
  const record: StoredConsent = {
    v: CONSENT_VERSION,
    ts: Date.now(),
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    vendors: [...declared.analytics, ...declared.marketing],
  };
  if (options.persist) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Private mode or a full quota: the choice still applies for this visit.
    }
  }
  applyConsent(prefs);
  window.dispatchEvent(new CustomEvent<ConsentPreferences>(CONSENT_CHANGE_EVENT, { detail: prefs }));
  return record;
}

export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}
